import React, { Fragment, useEffect, useRef, useState } from "react";
import { ReactComponent as ArrowBackIcon } from "assets/images/common/svgs/arrow_back.svg";
import { useNavigate } from "react-router-dom";
import useUtils from "hooks/useUtils";
import { useSelector } from "react-redux";
import useUsers from "containers/user-management/useHooks";
import CustomMessageDisplay from "components/particles/table/data-not-found/CustomMessageDisplay";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import editProfilePicUser from "assets/images/common/others/editProfileUSer.png";
import noDataFound from "assets/images/gifs/noFoundGif.gif";


const ViewCommunity = () => {
  const [event, setEvent] = useState<any>({});
  const { getQueryParams } = useUtils();
  const { getEventDetailById } = useUsers();
  const [messages, setMessages] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    count: 0,
    currentPage: 0,
    totalPages: 1,
  });
  const { getCommunityMessageById } = useUsers();

  const pageSize = 10;
  const containerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeight = useRef(0);
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const params = getQueryParams();
  const eventId = params?.id;

  useEffect(() => {
    getEventDetailById(params?.id, setEvent);
  }, []);
  console.log(params?.id);
  const navigate = useNavigate();
  const goToBack = () => {
    navigate(-1);
  };

  const loadMessages = (page: number) => {
    const container = containerRef.current;
    if (page > 0 && container) {
      // record scrollHeight before prepend
      prevScrollHeight.current = container.scrollHeight;
    }
    getCommunityMessageById(
      setMessages,
      eventId,
      setPagination,
      page,
      pageSize
    );
  };

  // initial load on eventId change
  useEffect(() => {
    setMessages([]);
    loadMessages(0);
  }, [eventId]);

  // adjust scroll after messages update
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (pagination.currentPage === 0) {
      // initial load → scroll to bottom (newest)
      container.scrollTop = container.scrollHeight;
    } else {
      // older messages prepended → keep view at same items
      const newHeight = container.scrollHeight;
      const diff = newHeight - prevScrollHeight.current;
      container.scrollTop = diff;
    }
  }, [messages, pagination.currentPage]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    // when user scrolls to near top, load older if any
    if (scrollTop <= 50 && pagination.currentPage + 1 < pagination.totalPages) {
      loadMessages(pagination.currentPage + 1);
    }
  };

  const formatDateHeading = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (d1: Date, d2: Date) =>
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();

    if (isSameDay(date, today)) return "Today";
    if (isSameDay(date, yesterday)) return "Yesterday";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (dateStr: string): string =>
    new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

  // for date headings
  let lastShownDate: string | null = null;

  const attendingCount = event?.event?.eventSubscriptions?.length || 0;
  const renderAttendingCount = () => {
    if (!attendingCount || attendingCount <= 0) {
      return <span className="text-base text-lightBlackText ml-2"></span>;
    }

    if (attendingCount > 3) {
      return (
        <span className="text-base text-lightBlackText ml-3">3+ attendees</span>
      );
    }

    return (
      <span className="text-base text-lightBlackText ml-3">
        {attendingCount} {attendingCount === 1 ? "attendee" : "attendees"}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center gap-4 mb-6">
        <h1 className="text-base text-primaryText font-bold flex items-center gap-3">
          <span
            onClick={goToBack}
            className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
          >
            <ArrowBackIcon />
          </span>
          {event?.event?.title || ""}
        </h1>
      </div>

      <div className="bg-white border-[0.3px] border-mediumGrayBorder rounded-[20px] p-6 my-6">
        <div className="flex flex-wrap gap-4 items-center">
          <h1 className="text-[20px] md:text-[26px] lg:text-[32px] font-bold text-primaryText">
            {event?.event?.title || ""}
          </h1>
          <p className="bg-peachFadeBg text-darkYellowText text-sm font-medium rounded-md  px-2 py-1">
            {event?.event?.isSession ? "Session" : "Workshop"}
          </p>
          <p className="text-sm font-medium text-captionText">
          {event?.event?.availableSlots}{" "}
          {event?.event?.availableSlots <= 1 ? "Slot" : "Slots"} Available
          </p>
        </div>
        <div className="flex items-center gap-4 my-3">
          <div
            className={`flex items-center ${
              attendingCount > 0 ? "" : "cursor-default"
            }`}
          >
            {attendingCount > 0 &&
              event?.event?.eventSubscriptions
                ?.slice(0, 3)
                .map((avatar: any, index: number) => (
                  <img
                    key={index}
                    src={
                      avatar?.profilePicUrl?.fileDownloadUri
                        ? concatPathWithBackendUrl(
                            avatar.profilePicUrl.fileDownloadUri
                          )
                        : editProfilePicUser
                    }
                    alt="Attendee"
                    className="w-7 h-7 -mr-2 rounded-full border-2 border-white"
                  />
                ))}
            {renderAttendingCount()}
          </div>
        </div>
        <div className="">
          {/* Messages */}
          <div
            ref={containerRef}
            className="py-6 overflow-y-auto max-h-[500px]"
            onScroll={handleScroll}
          >
            {messages.map((msg) => {
              const msgDate = new Date(msg.createdAt).toDateString();
              const showHeading = msgDate !== lastShownDate;
              lastShownDate = msgDate;

              return (
                <React.Fragment key={msg.id}>
                  {showHeading && (
                    <div className="text-center text-xs text-uiGrayText my-4">
                      {formatDateHeading(msg.createdAt)}
                    </div>
                  )}
                  <div className="mb-5">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <img
                        src={msg.userProfilePic}
                        alt="Profile"
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0"
                      />
                      <div className="bg-culturedBg rounded-tl-none rounded-lg p-3 sm:px-4 max-w-full sm:max-w-2xl w-fit">
                        <div className="font-semibold text-sm text-primaryText mb-1">
                          {msg.userName}
                        </div>
                        <div className="text-sm text-primaryText whitespace-pre-line break-words">
                          {msg.message}
                        </div>
                        <div className="text-xs text-uiGrayText mt-1">
                          {formatTime(msg.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <Fragment>
          <CustomMessageDisplay
            show={!isLoading && !messages.length}
            title="No Messages Yet"
            slogan="There are currently no messages in this community."
            image={noDataFound}
            imageAlt="No Messages Found"
            className="py-12"
            imageClassName="w-24 h-24"
          />
        </Fragment>
      </div>
    </div>
  );
};

export default ViewCommunity;
